/**
 * useFeatureFlagConfig
 *
 * Single source of truth for all feature flag definitions.
 * Used by both the list page (ExtranetFeatureFlags) and the
 * detail page (ExtranetFeatureFlagDetail) to avoid repetition.
 *
 * Each config entry defines:
 *  - key          : matches the API `feature` field
 *  - name         : human-readable display name
 *  - description  : short description for list view
 *  - overview     : long-form paragraph for the detail overview section
 *  - scope        : 'corporate' | 'branch' | 'merchant' | 'system'
 *  - highlights   : array of { icon, title, desc } for the 3 feature cards
 *  - offBehavior  : array of strings describing what happens when flag is OFF
 *  - onBehavior   : array of strings describing what happens when flag is ON
 *  - hierarchy    : array[7] – 7-level inheritance chain (via makeHierarchy())
 */
export function useFeatureFlagConfig() {

  /**
   * HIERARCHY LEVEL ICONS — keyed by `levelKey`
   * Used by the detail page template.
   */
  const LEVEL_ICON_MAP = {
    superadmin: 'fas fa-user-shield',
    corporate: 'fas fa-building',
    area: 'fas fa-map-marked-alt',
    branch_head: 'fas fa-sitemap',
    branch: 'fas fa-code-branch',
    merchant_owner: 'fas fa-user-tie',
    merchant: 'fas fa-store',
    member: 'fas fa-mobile-alt'
  }

  const FLAG_CONFIGS = {
    // ─────────────── SYSTEM FLAGS ───────────────
    point_control: {
      key: 'point_control',
      name: 'Point Control Mode',
      description: 'Define the core membership point logic and hierarchy across the...',
      overview: 'Define the core membership point logic and hierarchy. This configuration determines how points are distributed across the organizational hierarchy. It allows you to toggle the point system OFF, or when ON can control at the corporate level for choose between Centralized (Centralize) all point plan on corporate and Decentralized (Decentralize) is each merchant have point plan of its own.\n Furthermore, toggling this module ON or OFF directly affects the member dashboard, showing or hiding point-related information for the end-users.',
      scope: 'system',
      highlights: [
        {
          icon: 'fas fa-sliders-h',
          title: 'Centralize or Decentralize',
          desc: 'Switch between centralized (corporate level has central point plan) and decentralized (per-merchant level has its own point plan) on corporate point setting.'
        },
        {
          icon: 'fas fa-share-alt',
          title: 'Cascade Effect',
          desc: 'All merchant point rules are immediately affected by this toggle.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Audit Integrity',
          desc: 'Every mode change is recorded for full traceability across the platform.'
        }
      ],
      offBehavior: [
        'Point Setting on Corporate Level is disable.',
        'No points are can be accumulated or redeemed by any merchant.',
        'All point-related menus are hidden from corporate and merchant dashboards.',
        'Point balances and history are completely hidden from the member dashboard.'
      ],
      onBehavior: [
        'Activates the point setting menu on corporate level.',
        'corporate and merchants can participate in point module.',
        'The points obtained from each transaction will use the point plan that has been set.',
        'Point balances and history become visible on the member dashboard.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        corporate: { label: 'Corporate Level', sublabel: 'Corporate Point Setting', badge: 'CORPORATE CONTROL', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Individual Merchant Point Plan', badge: 'INHERITED', isMaster: false },
        member: { label: 'Member Dashboard', sublabel: 'Page Visibility', badge: 'AFFECTED', isMaster: false }
      }
    },

    new_auth_module: {
      key: 'new_auth_module',
      name: 'New Auth Module',
      description: 'Activate the new transitional authentication system with separate...',
      overview: 'Activate the new transitional authentication system with separate login endpoints. This module introduces two new dedicated login pathways: \'/backoffice/login\' for administrators (Corporate, Branch, Merchant) and \'/member/login\' for members. The legacy \'/login\' endpoint remains fully functional and secure during this transition.',
      scope: 'system',
      highlights: [
        {
          icon: 'fas fa-route',
          title: 'Dedicated Endpoints',
          desc: 'Introduces separate login URLs for backoffice and member access.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Legacy Support',
          desc: 'The original \'/login\' endpoint continues to function securely without interruption.'
        },
        {
          icon: 'fas fa-exchange-alt',
          title: 'Safe Transition',
          desc: 'Enables gradual migration to new authentication routing without breaking existing flows.'
        }
      ],
      offBehavior: [
        'New login endpoints \'/backoffice/login\' and \'/member/login\' are disabled.',
        'All users must use the legacy \'/login\' endpoint.',
        'No new authentication pathways are exposed.'
      ],
      onBehavior: [
        'New \'/backoffice/login\' endpoint is open for admins (Corporate, Branch, Merchant).',
        'New \'/member/login\' endpoint is open for member dashboard access.',
        'Legacy \'/login\' endpoint remains active and secure for backwards compatibility.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        corporate: { label: 'Backoffice Login Page', sublabel: 'Admin Access (/backoffice/login)', badge: 'AFFECTED', isMaster: false },
        merchant: { label: 'Member Login Page', sublabel: 'Member Access (/member/login)', badge: 'AFFECTED', isMaster: false }
      }
    },

    // ─────────────── CORPORATE FLAGS ───────────────
    marketing_module: {
      key: 'marketing_module',
      name: 'Marketing Module',
      description: 'The Marketing Module serves as the centralized engine for brand...',
      overview: 'The Marketing Module serves as the centralized engine for brand amplification across the entire organizational ecosystem. It enables high-level administrators to manage core campaign tools and tracking who has accessed the marketing link and registered as a new member.\n This Flagging for controls the visibility of the Marketing menu, details, and campaigns across admin dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the marketing management page—including full campaign lists and details—for administrators across Corporate, Branch, and Merchant levels. When disabled (OFF), these menus and detail views are completely hidden from their respective dashboards.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu & Detail Visibility',
          desc: 'Directly controls whether the Marketing menu, campaigns, and detail pages are displayed or hidden.'
        },
        {
          icon: 'fas fa-sitemap',
          title: 'Multi-Level Control',
          desc: 'Affects menu and detail visibility across Corporate, Branch, and Merchant levels simultaneously.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing marketing campaigns and assets remain intact.'
        }
      ],
      offBehavior: [
        'Marketing menu and campaign details are hidden from the Corporate dashboard.',
        'Marketing menu and campaign details are hidden from Branch and Merchant dashboards.',
        'Administrators cannot access the marketing management page or view any active campaigns.'
      ],
      onBehavior: [
        'Marketing menu and campaign details become visible on the Corporate dashboard.',
        'Marketing menu and campaign details become visible on Branch and Merchant dashboards.',
        'Administrators regain full access to the marketing management page, campaigns, and details.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false }
      }
    },

    promotions_module: {
      key: 'promotions_module',
      name: 'Promotions Module',
      description: 'The Promotions Module is used to arrange highlight promotions for...',
      overview: 'The Promotions Module is used to arrange highlight promotions for vouchers, benefits, or both on the member dashboard, ensuring that when members access their dashboard, they can immediately see what is currently being promoted.\n This Flagging for controls the visibility of the Promotions menu across corporate dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the promotions management page for administrators across Corporate levels. When disabled (OFF), the menu is hidden from their respective dashboards. Additionally, this toggle directly impacts the member dashboard by showing or hiding the highlight promotions for end-users.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Promotions menu is displayed or hidden on the admin dashboard.'
        },
        {
          icon: 'fas fa-building',
          title: 'Corporate Exclusive',
          desc: 'This visibility toggle applies exclusively to the Corporate level dashboard.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing promotions data remains intact.'
        }
      ],
      offBehavior: [
        'Promotions menu is completely hidden from the Corporate dashboard.',
        'Administrators cannot access the promotions management page.',
        'Existing promotions data is preserved but inaccessible via UI.',
        'Highlight promotions and active offers are hidden from the member dashboard.'
      ],
      onBehavior: [
        'Promotions menu becomes visible on the Corporate dashboard.',
        'Administrators regain full access to the promotions management page.',
        'Highlight promotions and active offers are displayed prominently on the member dashboard.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        member: { label: 'Member Dashboard', sublabel: 'Page Visibility', badge: 'AFFECTED', isMaster: false }
      }
    },

    newsletter_module: {
      key: 'newsletter_module',
      name: 'Newsletter Module',
      description: 'The Newsletter Module is a membership CRM tool that functions to...',
      overview: 'The Newsletter Module is a membership CRM tool that functions to create and send promotional emails to all members or specific members based on their tier. The promotional content can be viewed directly via email or the member dashboard.\n This Flagging for controls the visibility of the Newsletter menu across admin dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the newsletter management page for administrators across Corporate, Branch, and Merchant levels. When disabled (OFF), the menu is hidden from their respective dashboards. Additionally, this toggle directly impacts the member dashboard by showing or hiding the newsletter content for end-users.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Newsletter menu is displayed or hidden on the admin dashboard.'
        },
        {
          icon: 'fas fa-sitemap',
          title: 'Multi-Level Control',
          desc: 'Affects menu visibility across Corporate, Branch, and Merchant levels simultaneously.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing newsletters and CRM data remain intact.'
        }
      ],
      offBehavior: [
        'Newsletter menu is hidden from the Corporate dashboard.',
        'Newsletter menu is hidden from Branch and Merchant dashboards.',
        'Administrators cannot access the newsletter management page.',
        'Promotional newsletter content is completely hidden from the member dashboard.'
      ],
      onBehavior: [
        'Newsletter menu becomes visible on the Corporate dashboard.',
        'Newsletter menu becomes visible on Branch and Merchant dashboards.',
        'Administrators regain full access to the newsletter management page.',
        'Members can view promotional newsletter content directly on their dashboard.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false },
        member: { label: 'Member Dashboard', sublabel: 'Page Visibility', badge: 'AFFECTED', isMaster: false }
      }
    },

    transaction_report_module: {
      key: 'transaction_report_module',
      name: 'Transaction Report Module',
      description: 'The Transaction Report Module provides a consolidated view of all...',
      overview: 'The Transaction Report Module provides a consolidated view of all financial transactions occurring across the corporate tenant ecosystem. It enables auditors and administrators to drill down from aggregate corporate summaries to individual merchant transactions, with full export capabilities and configurable date-range filtering.\n This Flagging for controls the visibility of the Transaction Report menu across admin dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the transaction report management page for administrators across Corporate and Branch levels. When disabled (OFF), the menu is hidden from their respective dashboards.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Transaction Report menu is displayed or hidden on the admin dashboard.'
        },
        {
          icon: 'fas fa-sitemap',
          title: 'Multi-Level Control',
          desc: 'Affects menu visibility across Corporate and Branch levels simultaneously.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing transaction reports remain intact.'
        }
      ],
      offBehavior: [
        'Transaction Report menu is hidden from the Corporate dashboard.',
        'Transaction Report menu is hidden from the Branch dashboard.',
        'Administrators cannot access the transaction report page.'
      ],
      onBehavior: [
        'Transaction Report menu becomes visible on the Corporate dashboard.',
        'Transaction Report menu becomes visible on the Branch dashboard.',
        'Administrators regain full access to the transaction report page.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false }
      }
    },

    benefit_module: {
      key: 'benefit_module',
      name: 'Benefit Module',
      description: 'The Benefit Module manages the reward and entitlement framework...',
      overview: 'The Benefit Module manages the reward and entitlement framework for members across all tiers. It enables corporate administrators to define tier-specific benefits — from exclusive discounts to priority access — ensuring a consistent and compelling loyalty experience.\n This flagging for controls the visibility of the Benefit menu across admin dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the benefit management page for administrators across Corporate, Branch, and Merchant levels. When disabled (OFF), the menu is hidden from their respective dashboards. Additionally, this toggle directly impacts the member dashboard by showing or hiding the available benefits for end-users.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Benefit menu is displayed or hidden on the admin dashboard.'
        },
        {
          icon: 'fas fa-sitemap',
          title: 'Multi-Level Control',
          desc: 'Affects menu visibility across Corporate, Branch, and Merchant levels simultaneously.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing benefit data remains intact.'
        }
      ],
      offBehavior: [
        'Benefit menu is hidden from the Corporate dashboard.',
        'Benefit menu is hidden from Branch and Merchant dashboards.',
        'Administrators cannot access the benefit management page.',
        'Benefit features and menus are completely hidden from the member dashboard.'
      ],
      onBehavior: [
        'Benefit menu becomes visible on the Corporate dashboard.',
        'Benefit menu becomes visible on Branch and Merchant dashboards.',
        'Administrators regain full access to the benefit management page.',
        'Members can view and access their available benefits on the member dashboard.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Menu Visibility', badge: 'INHERITED', isMaster: false },
        member: { label: 'Member Dashboard', sublabel: 'Page Visibility', badge: 'AFFECTED', isMaster: false }
      }
    },

    threshold_setting: {
      key: 'threshold_setting',
      name: 'Threshold Setting',
      description: 'Threshold Setting is used to send automated email alerts to all...',
      overview: 'Threshold Setting is used to send automated email alerts to members if their transaction total falls within the danger threshold range. This proactive notification system helps keep members informed about their tier status.\n This Flagging for controls the visibility of the Threshold Setting menu (which is located within the Member Tier menu) across corporate dashboards. This module acts as a straightforward display toggle. When activated (ON), it displays the threshold configuration page for administrators across Corporate levels. When disabled (OFF), the menu is hidden from their respective dashboards.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Threshold sub-menu is displayed or hidden under Member Tier.'
        },
        {
          icon: 'fas fa-building',
          title: 'Corporate Exclusive',
          desc: 'This visibility toggle applies exclusively to the Corporate level dashboard.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing threshold alert rules remain intact.'
        }
      ],
      offBehavior: [
        'Threshold Setting menu is completely hidden from the Member Tier section on the Corporate dashboard.',
        'Administrators cannot access the threshold configuration page.',
        'Existing threshold data is preserved but inaccessible via UI.'
      ],
      onBehavior: [
        'Threshold Setting menu becomes visible under the Member Tier section on the Corporate dashboard.',
        'Administrators regain full access to the threshold configuration page.',
        'Threshold alerts can be configured for member transactions.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Member Tier Sub-page', badge: 'MASTER CONTROL', isMaster: true },
        branch_head: { label: 'Member Tier', sublabel: 'Threshold Setting page', badge: 'INHERITED', isMaster: false }
      }
    },

    // ─────────────── BRANCH FLAGS ───────────────
    branch_product_management: {
      key: 'branch_product_management',
      name: 'Branch Product Management',
      description: 'The Branch Product Management feature allows branch administrators to...',
      overview: 'The Branch Product Management feature allows branch administrators to create, edit, and delete products across all merchants owned by the branch. The purpose of this feature is to centralize product control at the branch level, enabling branch administrators to manage product catalogs without needing to navigate into each individual merchant dashboard.\n This Flagging for controls the visibility of the Branch Product menu on the branch dashboard. This module acts as a straightforward display toggle. When activated (ON), it displays the product management page for branch-level administrators, allowing them to manage products for all merchants under their branch. When disabled (OFF), the Branch Product menu is hidden from the branch dashboard and the page shows a disabled status.',
      scope: 'branch',
      highlights: [
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Branch Product menu is displayed or hidden on the branch dashboard.'
        },
        {
          icon: 'fas fa-code-branch',
          title: 'Centralized Branch Control',
          desc: 'Branch administrators can manage products for all merchants under their branch from a single page.'
        },
        {
          icon: 'fas fa-link',
          title: 'POS Membership Dependency',
          desc: 'If both Branch and Merchant Product modules are disabled, POS Membership transaction creation will be disabled.'
        }
      ],
      offBehavior: [
        'Branch Product menu is hidden from the branch dashboard sidebar.',
        'The \'/branch/product\' page displays a disabled status message.',
        'Branch administrators cannot create, edit, or delete products from the branch level.',
        'If Merchant Product is also disabled, POS Membership transaction creation will be disabled.'
      ],
      onBehavior: [
        'Branch Product menu becomes visible on the branch dashboard sidebar.',
        'Branch administrators gain full access to the product management page.',
        'Products can be created, edited, and deleted for all merchants under the branch.',
        'Branch-level product control is centralized without needing to access each merchant individually.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Menu & Page Visibility', badge: 'CONTROLLED', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Product Data Source', badge: 'NOT AFFECTED', isMaster: false }
      }
    },

    product_management: {
      key: 'product_management',
      name: 'Product Management',
      description: 'Enable local branches to manage specific product inventory and...',
      overview: 'Product Management provides branch-level control over the product lifecycle, from listing to deactivation. It enables branch managers to respond to local market demands by maintaining an accurate, up-to-date product offering that aligns with corporate standards while reflecting regional preferences.',
      scope: 'branch',
      highlights: [
        {
          icon: 'fas fa-clipboard-list',
          title: 'Product Lifecycle',
          desc: 'Manage products from creation through to end-of-life at the branch level.'
        },
        {
          icon: 'fas fa-map-marker-alt',
          title: 'Regional Relevance',
          desc: 'Tailor product availability to local demand and inventory.'
        },
        {
          icon: 'fas fa-sync',
          title: 'Sync with Corporate',
          desc: 'Changes sync with the corporate catalog for unified reporting.'
        }
      ],
      offBehavior: [
        'Product management panel is inaccessible to branch admins.',
        'Only the corporate-defined catalog is visible.',
        'No local product modifications are permitted.'
      ],
      onBehavior: [
        'Branch admins can manage their full product catalog.',
        'Local product additions and edits are synced to central reporting.',
        'Inventory adjustments take effect immediately for members.'
      ],
      hierarchy: {
        corporate: { label: 'Corporate Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Direct Control', badge: 'CONTROLLED', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'N/A', badge: 'NOT APPLICABLE', isMaster: false }
      }
    },

    // ─────────────── MERCHANT FLAGS ───────────────
    merchant_product: {
      key: 'merchant_product',
      name: 'Merchant Product',
      description: 'Controls the visibility and access to the Product Management module..',
      overview: 'The Merchant Product feature provides individual merchants with the ability to manage their own product catalog directly from the merchant dashboard. Merchants can create new products, edit existing product details and pricing, and delete products as needed. This feature empowers each merchant to maintain an accurate and up-to-date product offering that reflects their unique business within the corporate loyalty ecosystem.\n This flag controls the visibility of the Product menu on the merchant dashboard. When activated (ON), merchants have full access to the product management pages including the product list (/product), product creation (/product/create), and product editing (/product/{id}). When disabled (OFF), the Product menu is hidden from the sidebar and all product management pages display a disabled status message, preventing any product operations at the merchant level.',
      scope: 'merchant',
      highlights: [
        {
          icon: 'fas fa-store',
          title: 'Storefront Control',
          desc: 'Merchants manage their own product listings and pricing directly from the dashboard.'
        },
        {
          icon: 'fas fa-eye',
          title: 'Menu Visibility',
          desc: 'Directly controls whether the Product menu is displayed or hidden on the merchant dashboard.'
        },
        {
          icon: 'fas fa-link',
          title: 'POS Membership Dependency',
          desc: 'If both Branch and Merchant Product modules are disabled, POS Membership transaction creation will be disabled.'
        }
      ],
      offBehavior: [
        'Product menu is hidden from the merchant dashboard sidebar.',
        'The \'/product\', \'/product/create\', and \'/product/{id}\' pages display a disabled status message.',
        'Merchants cannot create, edit, or delete products from the merchant level.',
        'If Branch Product is also disabled, POS Membership transaction creation will be disabled.'
      ],
      onBehavior: [
        'Product menu becomes visible on the merchant dashboard sidebar.',
        'Merchants gain full access to the product list, creation, and editing pages.',
        'Products can be created, edited, and deleted by the merchant.',
        'Active products are immediately visible and manageable from the merchant dashboard.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        branch: { label: 'Branch Level', sublabel: 'Regional Oversight', badge: 'SUPERVISOR', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Direct Control', badge: 'CONTROLLED', isMaster: false }
      }
    },

    merchant_access_token: {
      key: 'merchant_access_token',
      name: 'Access Token & Auth URL',
      description: 'Controls the visibility and access to the legacy Access Token..',
      overview: 'The Access Token & Auth URL feature allows merchants to generate personal Access Tokens and Authentication URLs needed for legacy external systems to connect with the Membership API. Because this is a legacy integration method that predates newer connection modules, it is often kept disabled to steer merchants toward modern integration pathways.\n This flag controls the visibility of both the "Access Token" and "Authentication URL" menus on the merchant dashboard. When activated (ON), merchants can access these pages to create and manage their tokens and URLs. When disabled (OFF), these menus are hidden from the sidebar and the pages display a disabled status message, preventing the creation of new legacy credentials while preserving any existing ones.',
      scope: 'merchant',
      highlights: [
        {
          icon: 'fas fa-key',
          title: 'Legacy Connectivity',
          desc: 'Manages access to the older token-based integration methods.'
        },
        {
          icon: 'fas fa-link',
          title: 'Menu Visibility',
          desc: 'Simultaneously hides or shows both Access Token and Authentication URL menus.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Data Preservation',
          desc: 'Toggling this flag only affects UI visibility; existing tokens and URLs remain intact.'
        }
      ],
      offBehavior: [
        'Access Token and Authentication URL menus are hidden from the merchant dashboard sidebar.',
        'The \'/user/access-token\' and \'/user/authentication-url\' pages display a disabled status message.',
        'Merchants cannot create or manage legacy access tokens or authentication URLs.',
        'Existing tokens remain preserved but are not manageable from the merchant dashboard.'
      ],
      onBehavior: [
        'Access Token and Authentication URL menus become visible on the merchant dashboard sidebar.',
        'Merchants gain full access to the token and URL management pages.',
        'Tokens and URLs can be created, viewed, and managed by the merchant.',
        'Active tokens remain fully functional for external API integration.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        corporate: { label: 'Corporate Level', sublabel: 'Regional Oversight', badge: 'CORPORATE CONTROL', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Direct Control', badge: 'CONTROLLED', isMaster: false }
      }
    },

    voucher_gift: {
      key: 'voucher_gift',
      name: 'Voucher Gift',
      description: 'Controls the visibility and access to the Voucher Gift module for..',
      overview: 'The Voucher Gift feature empowers merchants to directly reward members by issuing custom gift vouchers. This module serves as a powerful promotional tool, allowing merchants to give free vouchers upon registration, reward loyal customers, and run targeted retention campaigns right from their dashboard.\n This flag controls the visibility of both the "Voucher Gift" and "Voucher Gift History" menus on the merchant dashboard. Because vouchers are tightly integrated with the point system, this flag depends on the Point Control module—if point control is deactivated, the voucher gift module is automatically disabled. When activated (ON), merchants can create and distribute new gift vouchers and track their distribution history. When disabled (OFF), the menus are hidden and pages display a disabled status message, preventing the issuance of new gifts while preserving past distribution records.',
      scope: 'merchant',
      highlights: [
        {
          icon: 'fas fa-gift',
          title: 'Direct Rewarding',
          desc: 'Empowers merchants to gift vouchers directly to loyal members or new signups.'
        },
        {
          icon: 'fas fa-link',
          title: 'Point Dependency',
          desc: 'Automatically disables if the Point Control module is turned off.'
        },
        {
          icon: 'fas fa-history',
          title: 'Preserved Records',
          desc: 'Toggling this flag only hides the UI; past voucher gift distribution history remains intact.'
        }
      ],
      offBehavior: [
        'Voucher Gift and Gift History menus are hidden from the merchant dashboard sidebar.',
        'The \'/voucher/gift\' and \'/voucher/gift/list\' pages display a disabled status message.',
        'Merchants cannot issue new gift vouchers to members.',
        'Existing gift distribution history is preserved but not accessible via the UI.'
      ],
      onBehavior: [
        'Voucher Gift and Gift History menus become visible on the merchant dashboard sidebar.',
        'Merchants gain full access to the voucher gift issuance and history pages.',
        'New gift vouchers can be created and distributed directly to members.',
        'Distribution events are logged and trackable in the history page.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        corporate: { label: 'Corporate Level', sublabel: 'Regional Oversight', badge: 'CORPORATE CONTROL', isMaster: false },
        merchant: { label: 'Merchant Level', sublabel: 'Direct Control', badge: 'CONTROLLED', isMaster: false }
      }
    },

    // ─────────────── BOOKING ENGINE MODULE ───────────────
    booking_module: {
      key: 'booking_module',
      name: 'Booking Engine Module',
      description: 'Display connected Booking Engine properties and enable direct reservations on member dashboard.',
      overview: 'Controls the visibility and functionality of the Booking Engine integration. When active, connected hotel and resort properties are displayed on the member dashboard (Our Properties section), allowing members to browse accommodations and initiate direct bookings with auto-login session context.',
      scope: 'corporate',
      highlights: [
        {
          icon: 'fas fa-hotel',
          title: 'Direct Property Reservations',
          desc: 'Seamlessly connects loyalty members directly to the Booking Engine system.'
        },
        {
          icon: 'fas fa-toggle-on',
          title: 'Corporate Level Control',
          desc: 'Easily enable or disable the reservation section across member dashboards.'
        },
        {
          icon: 'fas fa-shield-alt',
          title: 'Contextual Auto-Login',
          desc: 'Transfers member session tokens securely via postMessage handshake.'
        }
      ],
      offBehavior: [
        'The Our Properties reservation card is completely hidden from Member Dashboard (desktop and mobile).',
        'Direct booking redirection endpoints and modals are disabled for members.'
      ],
      onBehavior: [
        'The Our Properties section is visible on Member Dashboard.',
        'Members can view connected hotel properties and launch direct booking flows.'
      ],
      hierarchy: {
        superadmin: { label: 'System Level', sublabel: 'Master Authority', badge: 'MASTER CONTROL', isMaster: true },
        corporate: { label: 'Corporate Level', sublabel: 'Regional Oversight', badge: 'CORPORATE CONTROL', isMaster: false }
      }
    },

  }

  /**
   * Get config for a specific feature key.
   * Returns null if not found.
   */
  function getConfig(featureKey) {
    return FLAG_CONFIGS[featureKey] || null
  }

  /**
   * Get all configs as an array.
   */
  function getAllConfigs() {
    return Object.values(FLAG_CONFIGS)
  }

  /**
   * Get configs filtered by scope.
   */
  function getConfigsByScope(scope) {
    return Object.values(FLAG_CONFIGS).filter(c => c.scope === scope)
  }

  return {
    FLAG_CONFIGS,
    LEVEL_ICON_MAP,
    getConfig,
    getAllConfigs,
    getConfigsByScope
  }
}
